// Bottom sheet: scrim + sheet sliding up (xmSlideUp), radius 28 on top, optional handle. Escape and the scrim close it.
// Focus moves into the sheet when it opens and back to where it was when it closes.
//   <BottomSheet open={open} onClose={() => setOpen(false)} label="When should we plan?">...</BottomSheet>
import type { ComponentChildren, JSX } from 'preact';
import { useEffect, useRef } from 'preact/hooks';
import { dismissToast } from '../store';

export interface BottomSheetProps {
  open: boolean;
  onClose: () => void;
  /** Accessible name of the dialog. */
  label: string;
  children: ComponentChildren;
  /** Drag-handle bar on top (the Stuck sheet has one). */
  handle?: boolean;
  /** Sheet background (default #1C1916; the Stuck sheet uses rgba(24,22,19,0.98)). */
  bg?: string;
  /** Scrim opacity (default 0.55). */
  scrim?: number;
  /** Close on scrim tap (default true). */
  dismissable?: boolean;
  style?: JSX.CSSProperties;
}

export function BottomSheet({ open, onClose, label, children, handle, bg = '#1C1916', scrim = 0.55, dismissable = true, style }: BottomSheetProps) {
  const sheet = useRef<HTMLDivElement>(null);
  useEffect(() => {
    if (!open) return undefined;
    // A toast from before belongs to the screen under the sheet: it goes, so it never sits on the sheet.
    dismissToast();
    const prev = document.activeElement as HTMLElement | null;
    const el = sheet.current;
    const first = el?.querySelector<HTMLElement>('button, [href], input, textarea, [tabindex]:not([tabindex="-1"])');
    (first || el)?.focus({ preventScroll: true });
    const onKey = (e: KeyboardEvent) => { if (e.key === 'Escape' && dismissable) { e.stopPropagation(); onClose(); } };
    document.addEventListener('keydown', onKey);
    return () => { document.removeEventListener('keydown', onKey); prev?.focus?.({ preventScroll: true }); };
  }, [open]);
  if (!open) return null;
  return (
    <div style={{ position: 'absolute', inset: 0, zIndex: 50 }}>
      <button type="button" aria-label="Close" tabIndex={-1} onClick={() => dismissable && onClose()} style={{ position: 'absolute', inset: 0, background: `rgba(0,0,0,${scrim})`, cursor: dismissable ? 'pointer' : 'default', animation: 'xmScreenIn 200ms ease-out both' }} />
      <div
        ref={sheet} role="dialog" aria-modal="true" aria-label={label} tabIndex={-1}
        class="xm-scroll xm-sheet"
        style={{
          position: 'absolute', left: 0, right: 0, bottom: 0, maxHeight: 'calc(100% - var(--xm-top) - 12px)',
          padding: `${handle ? 12 : 22}px calc(24px + var(--xm-sar)) calc(var(--xm-bottom) + 14px) calc(24px + var(--xm-sal))`,
          borderRadius: '28px 28px 0 0', background: bg, borderTop: '1px solid rgba(255,255,255,0.12)', boxShadow: '0 -12px 40px rgba(0,0,0,0.5)',
          display: 'flex', flexDirection: 'column', gap: 14, animation: 'xmSlideUp 300ms cubic-bezier(.2,.9,.3,1) both', outline: 'none', ...style,
        }}
      >
        {handle && <div aria-hidden="true" style={{ alignSelf: 'center', width: 36, height: 5, borderRadius: 3, background: 'rgba(255,255,255,0.3)', flex: 'none' }} />}
        {children}
      </div>
    </div>
  );
}
